import React, { useRef, useState, useEffect, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { QrCode, Printer, Download, Copy, CheckCircle2, UserPlus, HandCoins, LogIn, Link2, Sparkles, Trash2, Save, FolderOpen, Pencil, X, ScanLine, TrendingUp, CalendarDays, BarChart3, ArrowRightCircle, Upload, Bell, Plus, FileText, Users, Share2, GripVertical, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { printQrPoster } from "../lib/qrPoster";
import { useConfirm } from "../context/ConfirmContext";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const QR_CATEGORIES = ["General", "Events", "Giving", "Forms", "Courses", "Serve"];

const slug = (s) => (s || "qr").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "qr";

const download = (canvas, filename) => {
  if (!canvas) return;
  const a = document.createElement("a");
  a.href = canvas.toDataURL("image/png"); a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
};

const printQr = (canvas, title, caption, url) => printQrPoster(canvas, title, caption, url);

function QrCard({ testid, icon: Icon, title, caption, value, filename, subtitle, note, onDelete, onEdit, published, onTogglePublish, publishedLabel, expiresAt, recap }) {
  const ref = useRef(null);
  const [copied, setCopied] = useState(false);
  const canvasOf = () => ref.current?.querySelector("canvas");
  const expired = expiresAt && expiresAt < new Date().toISOString().slice(0, 10);
  const expLabel = expiresAt ? new Date(expiresAt + "T00:00:00").toLocaleDateString('en-GB', { day: "numeric", month: "short", year: "numeric" }) : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Link copied"); }
    catch { toast.error("Could not copy"); }
  };
  return (
    <div className={`${CARD} p-6 flex flex-col`} data-testid={testid}>
      <div className="flex items-start gap-2.5 mb-1">
        <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#0B5E4E1A" }}>
          <Icon className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-head text-lg font-bold truncate" style={{ color: "var(--sj-text)" }}>{title}</h2>
          {subtitle && <p className="text-xs mt-0.5" style={{ color: "var(--sj-text-3)" }}>{subtitle}</p>}
        </div>
        {(onEdit || onDelete) && (
          <div className="flex items-center gap-1 shrink-0">
            {onEdit && (
              <button type="button" data-testid={`${testid}-edit`} onClick={onEdit} title="Edit this QR code"
                className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-[var(--sj-bg)]" style={{ color: "var(--sj-primary)" }}>
                <Pencil className="h-4 w-4" />
              </button>
            )}
            {onDelete && (
              <button type="button" data-testid={`${testid}-delete`} onClick={onDelete} title="Delete saved QR code"
                className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-[#FBEAEA]" style={{ color: "#B91C1C" }}>
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
      {caption && <p className="text-sm mb-2 truncate" title={caption} style={{ color: "var(--sj-text-2)" }}>{subtitle ? `→ ${caption}` : caption}</p>}
      {expiresAt && (
        <span data-testid={`${testid}-expiry`} className="self-start inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 mb-2"
          style={expired ? { background: "#FBEAEA", color: "#B91C1C" } : { background: "var(--sj-bg)", color: "var(--sj-text-2)" }}>
          <CalendarDays className="h-3 w-3" /> {expired ? `Expired ${expLabel}` : `Expires ${expLabel}`}
        </span>
      )}
      {note && <p className="text-xs mb-2 italic px-2 py-1.5 rounded-lg" data-testid={`${testid}-note`} style={{ color: "var(--sj-text-2)", background: "var(--sj-bg)" }}>“{note}”</p>}
      <div className="flex justify-center py-2" ref={ref}>
        <div className="p-3.5 rounded-2xl border-2" style={{ borderColor: "var(--sj-primary)" }}>
          <QRCodeCanvas value={value} size={180} level="M" includeMargin={false} fgColor="#0B5E4E" />
        </div>
      </div>
      <button type="button" onClick={copy} data-testid={`${testid}-url`}
        className="mt-3 mx-auto max-w-full flex items-center gap-1.5 text-xs font-medium hover:underline px-2"
        style={{ color: "var(--sj-primary)" }}>
        {copied ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> : <Copy className="h-3.5 w-3.5 shrink-0" />}
        <span className="truncate">{value}</span>
      </button>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        <button type="button" data-testid={`${testid}-print`} onClick={() => printQr(canvasOf(), title, note || caption, value)}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white hover:brightness-105" style={{ background: "var(--sj-primary)" }}>
          <Printer className="h-4 w-4" /> Print poster
        </button>
        <button type="button" data-testid={`${testid}-download`} onClick={() => download(canvasOf(), filename)}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border hover:bg-[var(--sj-bg)]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
          <Download className="h-4 w-4" /> Download PNG
        </button>
      </div>
      {onTogglePublish && (
        <button type="button" data-testid={`${testid}-publish`} onClick={onTogglePublish}
          className="mt-3 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-colors"
          style={published
            ? { background: "#0B5E4E1A", borderColor: "var(--sj-primary)", color: "var(--sj-primary)" }
            : { borderColor: "var(--sj-border)", color: "var(--sj-text-3)" }}
          title={published ? "Manage who this is shared with, or stop sharing" : "Share this QR with members in their portal"}>
          {published ? <Users className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
          {publishedLabel || (published ? "Shared with members" : "Share with members")}
        </button>
      )}
      {recap && (
        <div className="mt-2 text-xs" data-testid={`${testid}-recap`} style={{ color: "var(--sj-text-3)" }}>
          {recap.everyone ? (
            <span>Visible to <span style={{ color: "var(--sj-text-2)", fontWeight: 600 }}>all church members</span></span>
          ) : (
            <div className="flex flex-wrap gap-1.5 justify-center">
              {(recap.teamNames || []).map((n) => (
                <span key={`t-${n}`} className="rounded-full px-2 py-0.5" style={{ background: "#0B5E4E1A", color: "var(--sj-primary)", fontWeight: 600 }}>{n}</span>
              ))}
              {(recap.memberNames || []).map((n) => (
                <span key={`m-${n}`} className="rounded-full px-2 py-0.5" style={{ background: "var(--sj-bg)", color: "var(--sj-text-2)" }}>{n}</span>
              ))}
              {(recap.teamNames || []).length === 0 && (recap.memberNames || []).length === 0 && <span>Custom audience</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ChurchQRCodes() {
  const origin = window.location.origin;
  const confirm = useConfirm();
  const [tab, setTab] = useState("codes");
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("General");
  const [expiry, setExpiry] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const previewRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/qr-codes");
      setSaved(data || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try { const { data } = await api.get("/qr-codes/connect-stats"); setStats(data); }
    catch (e) { /* ignore */ }
    finally { setStatsLoading(false); }
  }, []);
  useEffect(() => { if (tab === "stats") loadStats(); }, [tab, loadStats]);

  const [resp, setResp] = useState(null);
  const [respSaving, setRespSaving] = useState(false);
  const [flyerUploading, setFlyerUploading] = useState(false);
  const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `t${Date.now()}${Math.floor(Math.random() * 999)}`);
  const blankTemplate = (name) => ({ id: newId(), name: name || "New response", message: "", send_sms: false, send_email: false, info_qr_enabled: false, info_qr_label: "More about our church", info_qr_link: "" });
  const loadResp = useCallback(async () => {
    try {
      const { data } = await api.get("/connect-response");
      const d = data || {};
      let templates = d.templates && d.templates.length ? d.templates : [{ id: "default", name: "Default", message: "", send_sms: false, send_email: false, info_qr_enabled: false, info_qr_label: "More about our church", info_qr_link: "" }];
      setResp({
        enabled: !!d.enabled,
        active_id: d.active_id || templates[0].id,
        templates,
        auto_followup: d.auto_followup || { enabled: false, message: "", delay_hours: 24 },
      });
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { if (tab === "response" && !resp) loadResp(); }, [tab, resp, loadResp]);
  const saveResp = async () => {
    setRespSaving(true);
    try { await api.put("/connect-response", resp); toast.success("Connect response saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setRespSaving(false); }
  };
  const activeTpl = resp ? (resp.templates.find((t) => t.id === resp.active_id) || resp.templates[0]) : null;
  const setTpl = (patch) => setResp((p) => ({ ...p, templates: p.templates.map((t) => (t.id === p.active_id ? { ...t, ...patch } : t)) }));
  const setAF = (patch) => setResp((p) => ({ ...p, auto_followup: { ...p.auto_followup, ...patch } }));
  const addTemplate = () => setResp((p) => { const t = blankTemplate(`Response ${p.templates.length + 1}`); return { ...p, templates: [...p.templates, t], active_id: t.id }; });
  const deleteTemplate = async (id) => {
    if (resp.templates.length <= 1) { toast.error("Keep at least one response."); return; }
    const t = resp.templates.find((x) => x.id === id);
    const ok = await confirm({ title: "Delete this response?", description: `Are you sure you want to delete "${t?.name}"? This cannot be undone.`, confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    setResp((p) => { const remaining = p.templates.filter((x) => x.id !== id); return { ...p, templates: remaining, active_id: p.active_id === id ? remaining[0].id : p.active_id }; });
  };
  const uploadFlyer = async (file) => {
    if (!file) return;
    setFlyerUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/connect-flyer", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const full = `${origin}${data.url}`;
      setTpl({ info_qr_enabled: true, info_qr_link: full, info_qr_label: activeTpl?.info_qr_label || "Evangelism flyer" });
      toast.success("Flyer uploaded — info QR link set");
      loadFlyers();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setFlyerUploading(false); }
  };

  const [flyers, setFlyers] = useState([]);
  const [followupLog, setFollowupLog] = useState([]);
  const [tplStats, setTplStats] = useState({});
  const loadFlyers = useCallback(async () => { try { const { data } = await api.get("/connect-flyers"); setFlyers(data || []); } catch (e) { /* ignore */ } }, []);
  const loadLog = useCallback(async () => { try { const { data } = await api.get("/connect-response/followup-log"); setFollowupLog(data.log || []); } catch (e) { /* ignore */ } }, []);
  const loadTplStats = useCallback(async () => { try { const { data } = await api.get("/connect-response/template-stats"); setTplStats(data.by_template || {}); } catch (e) { /* ignore */ } }, []);
  useEffect(() => { if (tab === "response") { loadFlyers(); loadLog(); loadTplStats(); } }, [tab, loadFlyers, loadLog, loadTplStats]);
  const pickFlyer = (f) => setTpl({ info_qr_enabled: true, info_qr_link: `${origin}${f.url}`, info_qr_label: f.label || activeTpl?.info_qr_label || "Evangelism flyer" });

  const [renamingFlyer, setRenamingFlyer] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const startRename = (f) => { setRenamingFlyer(f.file_id); setRenameVal(f.label || ""); };
  const saveRename = async (f) => {
    const label = renameVal.trim();
    setFlyers((prev) => prev.map((x) => (x.file_id === f.file_id ? { ...x, label } : x)));
    setRenamingFlyer(null);
    try { await api.put(`/connect-flyers/${f.file_id}`, { label }); toast.success("Flyer name saved"); }
    catch (e) { toast.error("Could not rename flyer"); loadFlyers(); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString('en-GB', { day: "numeric", month: "short", year: "numeric" });
  };

  const trimmedName = name.trim();
  const trimmedValue = value.trim();

  const saveCode = async () => {
    if (!trimmedName || !trimmedValue) { toast.error("Please enter a name and a link or text."); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/qr-codes/${editingId}`, { name: trimmedName, value: trimmedValue, caption: caption.trim(), category, expires_at: expiry });
        toast.success(`Updated "${trimmedName}"`);
      } else {
        await api.post("/qr-codes", { name: trimmedName, value: trimmedValue, caption: caption.trim(), category, expires_at: expiry });
        toast.success(`Saved "${trimmedName}"`);
      }
      setName(""); setValue(""); setCaption(""); setCategory("General"); setExpiry(""); setEditingId(null);
      await load();
      setTab("codes");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save QR code");
    } finally { setSaving(false); }
  };

  const startEdit = (qr) => {
    setEditingId(qr.id);
    setName(qr.name);
    setValue(qr.value);
    setCaption(qr.caption || "");
    setCategory(qr.category || "General");
    setExpiry(qr.expires_at || "");
    setTab("generator");
  };

  const cancelEdit = () => { setEditingId(null); setName(""); setValue(""); setCaption(""); setCategory("General"); setExpiry(""); };

  const removeCode = async (qr) => {
    const ok = await confirm({
      title: "Delete this QR code?",
      description: `Are you sure you want to delete "${qr.name}"? This cannot be undone.`,
      confirmLabel: "Delete", tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/qr-codes/${qr.id}`);
      setSaved((prev) => prev.filter((x) => x.id !== qr.id));
      toast.success("QR code deleted");
    } catch (e) { toast.error("Could not delete"); }
  };

  // Audience sharing dialog
  const [audiences, setAudiences] = useState({ teams: [], members: [] });
  const [shareQr, setShareQr] = useState(null);
  const [shareType, setShareType] = useState("everyone");
  const [shareTeams, setShareTeams] = useState([]);
  const [shareMembers, setShareMembers] = useState([]);
  const [memberQuery, setMemberQuery] = useState("");
  const [sharingSaving, setSharingSaving] = useState(false);
  const loadAudiences = useCallback(async () => {
    try { const { data } = await api.get("/qr-share-audiences"); setAudiences({ teams: data.teams || [], members: data.members || [] }); }
    catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadAudiences(); }, [loadAudiences]);

  const openShare = (qr) => {
    setShareQr(qr);
    setShareType(qr.audience_type === "custom" ? "custom" : "everyone");
    setShareTeams(qr.audience_team_ids || []);
    setShareMembers(qr.audience_member_ids || []);
    setMemberQuery("");
  };
  const toggleIn = (arr, id) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const saveShare = async (publish) => {
    if (!shareQr) return;
    if (publish && shareType === "custom" && shareTeams.length === 0 && shareMembers.length === 0) {
      toast.error("Pick at least one team or member, or choose Everyone."); return;
    }
    setSharingSaving(true);
    const body = { published: publish, audience_type: shareType, audience_team_ids: shareTeams, audience_member_ids: shareMembers };
    try {
      await api.put(`/qr-codes/${shareQr.id}/publish`, body);
      setSaved((prev) => prev.map((x) => (x.id === shareQr.id ? { ...x, published: publish, audience_type: shareType, audience_team_ids: shareTeams, audience_member_ids: shareMembers } : x)));
      toast.success(publish ? `"${shareQr.name}" is now shared` : `"${shareQr.name}" is no longer shared`);
      setShareQr(null);
    } catch (e) { toast.error("Could not update sharing"); }
    finally { setSharingSaving(false); }
  };
  const audienceLabel = (qr) => {
    if (!qr.published) return "Share with members";
    if ((qr.audience_type || "everyone") !== "custom") return "Shared with everyone";
    const t = (qr.audience_team_ids || []).length, m = (qr.audience_member_ids || []).length;
    const parts = [];
    if (t) parts.push(`${t} team${t === 1 ? "" : "s"}`);
    if (m) parts.push(`${m} member${m === 1 ? "" : "s"}`);
    return `Shared · ${parts.join(" + ") || "custom"}`;
  };
  const shareRecap = (qr) => {
    if (!qr.published) return null;
    if ((qr.audience_type || "everyone") !== "custom") return { everyone: true };
    const teamNames = (qr.audience_team_ids || []).map((id) => audiences.teams.find((t) => t.id === id)?.name).filter(Boolean);
    const memberNames = (qr.audience_member_ids || []).map((id) => audiences.members.find((m) => m.id === id)?.name).filter(Boolean);
    return { everyone: false, teamNames, memberNames };
  };

  // Drag-to-reorder (controls the order members see shared codes)
  const [dragId, setDragId] = useState(null);
  const persistOrder = async (list) => {
    try { await api.put("/qr-codes/reorder", { order: list.map((x) => x.id) }); }
    catch (e) { toast.error("Could not save order"); load(); }
  };
  const onDropReorder = (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    setSaved((prev) => {
      const from = prev.findIndex((x) => x.id === dragId);
      const to = prev.findIndex((x) => x.id === targetId);
      if (from < 0 || to < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      persistOrder(next);
      return next;
    });
    setDragId(null);
  };

  const downloadPreview = () => {
    const c = previewRef.current?.querySelector("canvas");
    if (c) download(c, `${slug(trimmedName)}-qr.png`);
  };
  const printPreview = () => {
    const c = previewRef.current?.querySelector("canvas");
    if (c) printQr(c, trimmedName || "QR code", "", trimmedValue);
  };

  const TabBtn = ({ id, label }) => (
    <button type="button" data-testid={`qr-tab-${id}`} onClick={() => setTab(id)}
      className="px-4 py-2 rounded-full text-sm font-semibold transition-colors"
      style={tab === id ? { background: "var(--sj-primary)", color: "#fff" } : { color: "var(--sj-text-2)", border: "1px solid var(--sj-border)", background: "#fff" }}>
      {label}
    </button>
  );

  return (
    <div className="space-y-6 max-w-6xl" data-testid="church-qr-page">
      <div className="sj-page-hero">
        <h1 className="font-head text-3xl font-extrabold flex items-center gap-2" style={{ color: "var(--sj-text)" }}>
          <QrCode className="h-7 w-7" style={{ color: "var(--sj-primary)" }} /> Church QR Codes
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--sj-text-2)" }}>
          Print or download QR codes for key church links — perfect for posters, screens, bulletins and welcome desks.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="qr-tabs">
        <TabBtn id="codes" label="QR Codes" />
        <TabBtn id="generator" label="QR Code Generator" />
        <TabBtn id="stats" label="QR Scan Stats" />
        <TabBtn id="response" label="Connect Response" />
      </div>

      {tab === "codes" && (
        <div className="space-y-6" data-testid="qr-codes-tab">
          <div>
            <h2 className="font-head text-lg font-bold mb-3" style={{ color: "var(--sj-text)" }}>Church links</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              <QrCard testid="qr-first-timers" icon={Sparkles} title="First Timers & Visitors — Connect with us"
                caption="First time or just visiting? Scan to tell us and we'll welcome you."
                value={`${origin}/first-timers?src=qr`} filename="first-timers-visitors-qr.png" />
              <QrCard testid="qr-connect" icon={UserPlus} title="Evangelism — Connect with us"
                caption="Met us on outreach? Scan to share your details and we'll be in touch."
                value={`${origin}/connect?src=qr`} filename="evangelism-connect-qr.png" />
              <QrCard testid="qr-giving" icon={HandCoins} title="Online Giving"
                caption="Scan to give your tithes and offerings securely (opens once Giving is switched on in Portal Settings)."
                value={`${origin}/giving`} filename="giving-qr.png" />
              <QrCard testid="qr-portal" icon={LogIn} title="Member Portal Login"
                caption="Members scan to sign in to their portal and courses."
                value={`${origin}/login`} filename="member-portal-qr.png" />
              <QrCard testid="qr-cell-form" icon={MapPin} title="Join a Cell Group"
                caption="Scan to fill in the Cell Group response form and we'll connect you with a group near you."
                value={`${origin}/cell-response`} filename="cell-group-form-qr.png" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="font-head text-lg font-bold" style={{ color: "var(--sj-text)" }}>Saved QR codes</h2>
              <button type="button" data-testid="qr-goto-generator" onClick={() => setTab("generator")}
                className="text-sm font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>+ Create new</button>
            </div>
            {loading ? (
              <div className={`${CARD} p-10 text-center text-sm`} style={{ color: "var(--sj-text-3)" }}>Loading…</div>
            ) : saved.length === 0 ? (
              <div className={`${CARD} p-10 text-center`} data-testid="qr-saved-empty">
                <FolderOpen className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--sj-text-3)" }} />
                <p className="text-sm font-semibold" style={{ color: "var(--sj-text-2)" }}>No saved QR codes yet</p>
                <p className="text-sm mt-1" style={{ color: "var(--sj-text-3)" }}>Use the QR Code Generator tab to create, name and save your own.</p>
              </div>
            ) : (
              <>
                <p className="text-xs mb-3 flex items-center gap-1.5" data-testid="qr-reorder-hint" style={{ color: "var(--sj-text-3)" }}>
                  <GripVertical className="h-3.5 w-3.5" /> Drag cards to set the order members see shared codes.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="qr-saved-grid">
                  {saved.map((qr) => (
                    <div key={qr.id} draggable data-testid={`qr-drag-${qr.id}`}
                      onDragStart={() => setDragId(qr.id)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onDropReorder(qr.id)}
                      className={`transition-opacity ${dragId === qr.id ? "opacity-40" : ""}`}>
                      <QrCard testid={`qr-saved-${qr.id}`} icon={QrCode} title={qr.name}
                        subtitle={`${qr.category ? qr.category + " · " : ""}${qr.scans || 0} scan${(qr.scans || 0) === 1 ? "" : "s"}${qr.created_by ? ` · Saved by ${qr.created_by}` : ""}`}
                        caption={qr.value} note={qr.caption} value={`${origin}/api/qr/${qr.id}`} filename={`${slug(qr.name)}-qr.png`}
                        expiresAt={qr.expires_at} recap={shareRecap(qr)}
                        published={!!qr.published} onTogglePublish={() => openShare(qr)} publishedLabel={audienceLabel(qr)}
                        onEdit={() => startEdit(qr)} onDelete={() => removeCode(qr)} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "generator" && (
        <div className={`${CARD} p-6`} data-testid="qr-generator-tab">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2.5">
              <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#0B5E4E1A" }}>
                <Link2 className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
              </span>
              <h2 className="font-head text-lg font-bold" style={{ color: "var(--sj-text)" }}>{editingId ? "Edit QR code" : "Create a QR code"}</h2>
            </div>
            {editingId && (
              <button type="button" data-testid="qr-gen-cancel-edit" onClick={cancelEdit}
                className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline" style={{ color: "var(--sj-text-3)" }}>
                <X className="h-4 w-4" /> Cancel edit
              </button>
            )}
          </div>
          <p className="text-sm mb-5" style={{ color: "var(--sj-text-2)" }}>
            {editingId
              ? "Update the name or link below — changes apply everywhere this QR is used."
              : "Give it a name, paste any link or text (event page, WhatsApp group, YouTube channel…), then save it so your team can reprint it any time."}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--sj-text-2)" }}>Name</label>
                <input data-testid="qr-gen-name" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Youth Camp Registration"
                  className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm px-4 py-2.5 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--sj-text-2)" }}>Link or text</label>
                <input data-testid="qr-gen-value" value={value} onChange={(e) => setValue(e.target.value)}
                  placeholder="https://…  or any text"
                  className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm px-4 py-2.5 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--sj-text-2)" }}>Description / note <span className="font-normal" style={{ color: "var(--sj-text-3)" }}>(shown to members)</span></label>
                <input data-testid="qr-gen-caption" value={caption} onChange={(e) => setCaption(e.target.value)}
                  placeholder="e.g. Scan to register for the Youth Camp"
                  className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm px-4 py-2.5 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--sj-text-2)" }}>Category <span className="font-normal" style={{ color: "var(--sj-text-3)" }}>(helps members find it)</span></label>
                <select data-testid="qr-gen-category" value={category} onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm px-4 py-2.5 text-sm">
                  {QR_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--sj-text-2)" }}>Expiry date <span className="font-normal" style={{ color: "var(--sj-text-3)" }}>(optional — auto-hidden from members after this date)</span></label>
                <div className="flex items-center gap-2">
                  <input type="date" data-testid="qr-gen-expiry" value={expiry} onChange={(e) => setExpiry(e.target.value)}
                    className="flex-1 bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm px-4 py-2.5 text-sm" />
                  {expiry && (
                    <button type="button" data-testid="qr-gen-expiry-clear" onClick={() => setExpiry("")}
                      className="text-xs font-semibold hover:underline" style={{ color: "var(--sj-text-3)" }}>Clear</button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button type="button" data-testid="qr-gen-save" onClick={saveCode} disabled={saving || !trimmedName || !trimmedValue}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white hover:brightness-105 disabled:opacity-50" style={{ background: "var(--sj-primary)" }}>
                  <Save className="h-4 w-4" /> {saving ? "Saving…" : (editingId ? "Update QR code" : "Save QR code")}
                </button>
                <button type="button" data-testid="qr-gen-download" onClick={downloadPreview} disabled={!trimmedValue}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold border hover:bg-[var(--sj-bg)] disabled:opacity-50" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                  <Download className="h-4 w-4" /> Download
                </button>
                <button type="button" data-testid="qr-gen-print" onClick={printPreview} disabled={!trimmedValue}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold border hover:bg-[var(--sj-bg)] disabled:opacity-50" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                  <Printer className="h-4 w-4" /> Print
                </button>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center" data-testid="qr-gen-preview" ref={previewRef}>
              {trimmedValue ? (
                <>
                  <div className="p-3.5 rounded-2xl border-2" style={{ borderColor: "var(--sj-primary)" }}>
                    <QRCodeCanvas value={trimmedValue} size={200} level="M" includeMargin={false} fgColor="#0B5E4E" />
                  </div>
                  {trimmedName && <p className="mt-3 font-semibold text-sm" style={{ color: "var(--sj-text)" }}>{trimmedName}</p>}
                  <p className="text-xs mt-0.5 max-w-[220px] text-center break-all" style={{ color: "var(--sj-text-3)" }}>{trimmedValue}</p>
                </>
              ) : (
                <div className="p-3.5 rounded-2xl border-2 border-dashed opacity-40" style={{ borderColor: "var(--sj-primary)" }}>
                  <div className="h-[200px] w-[200px] flex items-center justify-center" style={{ color: "var(--sj-text-3)" }}>
                    <div className="text-center">
                      <QrCode className="h-16 w-16 mx-auto" />
                      <p className="text-xs mt-2 flex items-center gap-1 justify-center"><Sparkles className="h-3.5 w-3.5" /> Live preview</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "stats" && (
        <div className="space-y-6" data-testid="qr-stats-tab">
          <div className="flex items-center gap-2.5">
            <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#0B5E4E1A" }}>
              <ScanLine className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
            </span>
            <div>
              <h2 className="font-head text-lg font-bold" style={{ color: "var(--sj-text)" }}>Connect QR — poster reach</h2>
              <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>How many people have joined by scanning the First Timers &amp; Visitors and Evangelism QR codes.</p>
            </div>
          </div>

          {statsLoading && !stats ? (
            <div className={`${CARD} p-10 text-center text-sm`} style={{ color: "var(--sj-text-3)" }}>Loading…</div>
          ) : !stats ? (
            <div className={`${CARD} p-10 text-center text-sm`} style={{ color: "var(--sj-text-3)" }}>No data yet.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="qr-stats-cards">
                {[
                  { key: "total", label: "Total via QR", value: stats.total, icon: ScanLine },
                  { key: "this_month", label: "This month", value: stats.this_month, icon: CalendarDays },
                  { key: "last7", label: "Last 7 days", value: stats.last7, icon: TrendingUp },
                  { key: "contacted", label: "Contacted", value: stats.contacted, icon: CheckCircle2 },
                  { key: "converted", label: "Moved to Pastoral Care", value: stats.converted, icon: ArrowRightCircle },
                ].map((c) => (
                  <div key={c.key} className={`${CARD} p-5`} data-testid={`qr-stat-${c.key}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <c.icon className="h-4 w-4" style={{ color: "var(--sj-primary)" }} />
                      <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>{c.label}</span>
                    </div>
                    <div className="text-3xl font-extrabold" style={{ color: "var(--sj-text)" }}>{c.value}</div>
                  </div>
                ))}
              </div>

              {stats.by_form && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="qr-stats-by-form">
                  <div className={`${CARD} p-5`} data-testid="qr-stat-first-timers">
                    <div className="flex items-center gap-2 mb-2"><Sparkles className="h-4 w-4" style={{ color: "#8a6a00" }} /><span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>First Timers &amp; Visitors QR</span></div>
                    <div className="text-3xl font-extrabold" data-testid="qr-stat-first-timers-total">{stats.by_form.first_timers.total}</div>
                    <p className="text-xs mt-1" style={{ color: "var(--sj-text-2)" }}>{stats.by_form.first_timers.first_time} first time · {stats.by_form.first_timers.just_visiting} just visiting · {stats.by_form.first_timers.this_month} this month</p>
                  </div>
                  <div className={`${CARD} p-5`} data-testid="qr-stat-evangelism">
                    <div className="flex items-center gap-2 mb-2"><UserPlus className="h-4 w-4" style={{ color: "var(--sj-primary)" }} /><span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Evangelism QR</span></div>
                    <div className="text-3xl font-extrabold" data-testid="qr-stat-evangelism-total">{stats.by_form.evangelism.total}</div>
                    <p className="text-xs mt-1" style={{ color: "var(--sj-text-2)" }}>{stats.by_form.evangelism.this_month} this month</p>
                  </div>
                </div>
              )}

              <div className={`${CARD} p-6`} data-testid="qr-stats-chart">
                <div className="flex items-center gap-2 mb-5">
                  <BarChart3 className="h-4 w-4" style={{ color: "var(--sj-primary)" }} />
                  <h3 className="font-head text-base font-bold" style={{ color: "var(--sj-text)" }}>QR sign-ups — last 6 months</h3>
                </div>
                {(() => {
                  const max = Math.max(1, ...stats.series.map((s) => s.count));
                  return (
                    <div className="flex items-end justify-between gap-3 h-48">
                      {stats.series.map((s, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center justify-end gap-2 h-full" data-testid={`qr-bar-${i}`}>
                          <span className="text-xs font-bold" style={{ color: "var(--sj-text-2)" }}>{s.count}</span>
                          <div className="w-full rounded-t-lg transition-all" style={{ height: `${Math.max(4, (s.count / max) * 100)}%`, background: s.count ? "var(--sj-primary)" : "var(--sj-border)", minHeight: "6px" }} title={`${s.count} in ${s.label}`} />
                          <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>{s.label}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              <div className={`${CARD} p-6`} data-testid="qr-stats-recent">
                <h3 className="font-head text-base font-bold mb-4" style={{ color: "var(--sj-text)" }}>Recent QR sign-ups</h3>
                {stats.recent.length === 0 ? (
                  <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>No one has joined via the Connect QR yet. Print the poster from the QR Codes tab to get started.</p>
                ) : (
                  <div className="space-y-2">
                    {stats.recent.map((r, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-xl px-3 py-2.5" data-testid={`qr-recent-${i}`} style={{ background: "var(--sj-bg)" }}>
                        <span className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-bold" style={{ background: "var(--sj-primary)" }}>
                          {(r.name || "?").charAt(0).toUpperCase()}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate" style={{ color: "var(--sj-text)" }}>{r.name}</div>
                          <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{fmtDate(r.created_at)}</div>
                        </div>
                        {r.contacted
                          ? <span className="text-[11px] font-semibold rounded-full px-2.5 py-1" style={{ background: "#0B5E4E1A", color: "var(--sj-primary)" }}>Contacted</span>
                          : <span className="text-[11px] font-semibold rounded-full px-2.5 py-1" style={{ background: "#FBEAD7", color: "#B45309" }}>Awaiting</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {saved.length > 0 && (
                <div className={`${CARD} p-6`} data-testid="qr-scans-by-poster">
                  <h3 className="font-head text-base font-bold mb-4" style={{ color: "var(--sj-text)" }}>Scans by poster</h3>
                  {(() => {
                    const rows = [...saved].sort((a, b) => (b.scans || 0) - (a.scans || 0));
                    const max = Math.max(1, ...rows.map((r) => r.scans || 0));
                    return (
                      <div className="space-y-3">
                        {rows.map((r) => (
                          <div key={r.id} className="flex items-center gap-3" data-testid={`qr-poster-scans-${r.id}`}>
                            <span className="w-40 shrink-0 truncate text-sm font-medium" style={{ color: "var(--sj-text)" }}>{r.name}</span>
                            <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: "var(--sj-bg)" }}>
                              <div className="h-full rounded-full transition-all" style={{ width: `${((r.scans || 0) / max) * 100}%`, background: "var(--sj-primary)", minWidth: r.scans ? "6px" : 0 }} />
                            </div>
                            <span className="w-16 text-right text-sm font-bold" style={{ color: "var(--sj-text-2)" }}>{r.scans || 0}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  <p className="text-xs mt-4" style={{ color: "var(--sj-text-3)" }}>Each saved QR uses its own tracking link, so every poster is counted separately.</p>
                </div>
              )}

              {(() => {
                const pubs = saved.filter((s) => s.published);
                return (
                  <div className={`${CARD} p-6`} data-testid="qr-member-leaderboard">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="h-4 w-4" style={{ color: "var(--sj-primary)" }} />
                      <h3 className="font-head text-base font-bold" style={{ color: "var(--sj-text)" }}>Shared with members — most opened</h3>
                    </div>
                    <p className="text-xs mb-4" style={{ color: "var(--sj-text-3)" }}>How many times members have tapped "Open link" on each QR you've shared in their portal.</p>
                    {pubs.length === 0 ? (
                      <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>You haven't shared any QR codes with members yet. On the QR Codes tab, tap "Share with members" on a saved QR.</p>
                    ) : (() => {
                      const rows = [...pubs].sort((a, b) => (b.portal_opens || 0) - (a.portal_opens || 0));
                      const max = Math.max(1, ...rows.map((r) => r.portal_opens || 0));
                      return (
                        <div className="space-y-3">
                          {rows.map((r, i) => (
                            <div key={r.id} className="flex items-center gap-3" data-testid={`qr-member-opens-${r.id}`}>
                              <span className="w-6 shrink-0 text-sm font-bold" style={{ color: i < 3 ? "var(--sj-primary)" : "var(--sj-text-3)" }}>{i + 1}</span>
                              <span className="w-40 shrink-0 truncate text-sm font-medium" style={{ color: "var(--sj-text)" }}>{r.name}</span>
                              <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: "var(--sj-bg)" }}>
                                <div className="h-full rounded-full transition-all" style={{ width: `${((r.portal_opens || 0) / max) * 100}%`, background: "var(--sj-primary)", minWidth: r.portal_opens ? "6px" : 0 }} />
                              </div>
                              <span className="w-16 text-right text-sm font-bold" style={{ color: "var(--sj-text-2)" }}>{r.portal_opens || 0}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {tab === "response" && (
        <div data-testid="qr-response-tab">
          {!resp || !activeTpl ? (
            <div className={`${CARD} p-10 text-center text-sm`} style={{ color: "var(--sj-text-3)" }}>Loading…</div>
          ) : (
            <div className="space-y-6">
              <div className={`${CARD} p-5`}>
                <label className="flex items-center gap-3 cursor-pointer mb-4" data-testid="resp-enabled">
                  <input type="checkbox" checked={!!resp.enabled} onChange={(e) => setResp((p) => ({ ...p, enabled: e.target.checked }))} className="h-4 w-4 accent-[#0B5E4E]" />
                  <span className="text-sm font-semibold" style={{ color: "var(--sj-text)" }}>Enable automated thank-you response on the Connect form</span>
                </label>
                <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--sj-text-3)" }}>Responses — the active one is used</p>
                <div className="flex flex-wrap items-center gap-2" data-testid="resp-templates">
                  {resp.templates.map((t) => (
                    <span key={t.id} className="inline-flex items-center gap-1 rounded-full pl-1 pr-1 py-1 text-xs font-medium"
                      style={t.id === resp.active_id ? { background: "var(--sj-primary)", color: "#fff" } : { background: "#0B5E4E0F", color: "var(--sj-primary)", border: "1px solid #0B5E4E33" }}>
                      <button type="button" data-testid={`resp-template-${t.id}`} onClick={() => setResp((p) => ({ ...p, active_id: t.id }))} className="px-2 font-semibold">{t.name}{tplStats[t.id] ? ` · ${tplStats[t.id]}` : ""}{t.id === resp.active_id ? " ✓" : ""}</button>
                      {resp.templates.length > 1 && (
                        <button type="button" data-testid={`resp-template-del-${t.id}`} onClick={() => deleteTemplate(t.id)} title="Delete response"
                          className="h-4 w-4 inline-flex items-center justify-center rounded-full" style={{ color: t.id === resp.active_id ? "#fff" : "#B91C1C" }}><X className="h-3 w-3" /></button>
                      )}
                    </span>
                  ))}
                  <button type="button" data-testid="resp-add-template" onClick={addTemplate}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                    <Plus className="h-3.5 w-3.5" /> Add response
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className={`${CARD} p-6 space-y-5`}>
                  <div className="flex items-center gap-2.5">
                    <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#0B5E4E1A" }}>
                      <UserPlus className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
                    </span>
                    <h2 className="font-head text-lg font-bold" style={{ color: "var(--sj-text)" }}>Edit response</h2>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--sj-text-2)" }}>Response name</label>
                    <input data-testid="resp-name" value={activeTpl.name || ""} onChange={(e) => setTpl({ name: e.target.value })}
                      placeholder="e.g. Easter service"
                      className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm px-4 py-2.5 text-sm" />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--sj-text-2)" }}>Thank-you message</label>
                    <textarea data-testid="resp-message" rows={3} value={activeTpl.message || ""} onChange={(e) => setTpl({ message: e.target.value })}
                      placeholder="Thank you for connecting with us! Someone from our team will reach out soon."
                      className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm px-4 py-2.5 text-sm" />
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-semibold" style={{ color: "var(--sj-text-2)" }}>Also send the message by</p>
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="resp-send-sms">
                      <input type="checkbox" checked={!!activeTpl.send_sms} onChange={(e) => setTpl({ send_sms: e.target.checked })} className="h-4 w-4 accent-[#0B5E4E]" />
                      <span className="text-sm" style={{ color: "var(--sj-text)" }}>SMS to their phone number</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="resp-send-email">
                      <input type="checkbox" checked={!!activeTpl.send_email} onChange={(e) => setTpl({ send_email: e.target.checked })} className="h-4 w-4 accent-[#0B5E4E]" />
                      <span className="text-sm" style={{ color: "var(--sj-text)" }}>Email to their email address</span>
                    </label>
                  </div>

                  <div className="pt-2 border-t" style={{ borderColor: "var(--sj-border)" }}>
                    <label className="flex items-center gap-3 cursor-pointer mb-3" data-testid="resp-infoqr-enabled">
                      <input type="checkbox" checked={!!activeTpl.info_qr_enabled} onChange={(e) => setTpl({ info_qr_enabled: e.target.checked })} className="h-4 w-4 accent-[#0B5E4E]" />
                      <span className="text-sm font-semibold" style={{ color: "var(--sj-text)" }}>Show an information QR on the thank-you screen</span>
                    </label>
                    {activeTpl.info_qr_enabled && (
                      <div className="space-y-3 pl-1">
                        <div>
                          <label className="block text-xs font-semibold mb-1" style={{ color: "var(--sj-text-3)" }}>Label</label>
                          <input data-testid="resp-infoqr-label" value={activeTpl.info_qr_label || ""} onChange={(e) => setTpl({ info_qr_label: e.target.value })}
                            placeholder="More about our church" className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm px-4 py-2 text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1" style={{ color: "var(--sj-text-3)" }}>Link</label>
                          <input data-testid="resp-infoqr-link" value={activeTpl.info_qr_link || ""} onChange={(e) => setTpl({ info_qr_link: e.target.value })}
                            placeholder="https://…  or upload a flyer below" className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm px-4 py-2 text-sm" />
                        </div>
                        <label data-testid="resp-flyer-upload" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border cursor-pointer hover:bg-[var(--sj-bg)]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                          <Upload className="h-4 w-4" /> {flyerUploading ? "Uploading…" : "Upload flyer (PDF/image)"}
                          <input type="file" accept=".pdf,image/*" className="hidden" disabled={flyerUploading} onChange={(e) => uploadFlyer(e.target.files?.[0])} />
                        </label>
                        <p className="text-xs" style={{ color: "var(--sj-text-3)" }}>Uploading a flyer sets the info QR link to that file automatically.</p>
                        {flyers.length > 0 && (
                          <div data-testid="flyer-library">
                            <p className="text-xs font-semibold mb-1.5" style={{ color: "var(--sj-text-3)" }}>Flyer library — tap to use</p>
                            <div className="grid grid-cols-3 gap-2">
                              {flyers.map((f) => {
                                const selected = (activeTpl.info_qr_link || "").includes(f.file_id);
                                const displayName = f.label || f.filename;
                                return (
                                  <div key={f.file_id} className="rounded-lg border-2 p-1.5 transition-colors" style={{ borderColor: selected ? "var(--sj-primary)" : "var(--sj-border)" }}>
                                    <button type="button" data-testid={`flyer-${f.file_id}`} onClick={() => pickFlyer(f)} title={displayName} className="block w-full text-left">
                                      {f.is_pdf ? (
                                        <div className="h-16 flex items-center justify-center rounded" style={{ background: "var(--sj-bg)" }}>
                                          <FileText className="h-7 w-7" style={{ color: "var(--sj-primary)" }} />
                                        </div>
                                      ) : (
                                        <img src={`${origin}${f.url}`} alt={displayName} className="h-16 w-full object-cover rounded" />
                                      )}
                                    </button>
                                    {renamingFlyer === f.file_id ? (
                                      <input autoFocus data-testid={`flyer-rename-input-${f.file_id}`} value={renameVal}
                                        onChange={(e) => setRenameVal(e.target.value)}
                                        onBlur={() => saveRename(f)}
                                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(f); if (e.key === "Escape") setRenamingFlyer(null); }}
                                        placeholder="Friendly name" className="mt-1 w-full text-[10px] bg-white border border-[#0B5E4E] rounded px-1.5 py-1 outline-none" />
                                    ) : (
                                      <button type="button" data-testid={`flyer-rename-${f.file_id}`} onClick={() => startRename(f)}
                                        className="mt-1 w-full flex items-center gap-1 text-[10px] hover:underline" style={{ color: "var(--sj-text-2)" }} title="Rename this flyer">
                                        <span className="truncate flex-1">{displayName}</span>
                                        <Pencil className="h-2.5 w-2.5 shrink-0" style={{ color: "var(--sj-primary)" }} />
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <button type="button" data-testid="resp-save" onClick={saveResp} disabled={respSaving}
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white hover:brightness-105 disabled:opacity-50" style={{ background: "var(--sj-primary)" }}>
                    <Save className="h-4 w-4" /> {respSaving ? "Saving…" : "Save all"}
                  </button>
                </div>

                <div className="space-y-6">
                  <div className={`${CARD} p-6`} data-testid="resp-preview">
                    <h3 className="font-head text-base font-bold mb-4" style={{ color: "var(--sj-text)" }}>Preview</h3>
                    {resp.enabled ? (
                      <div className="rounded-2xl p-6 text-center" style={{ background: "var(--sj-bg)" }}>
                        <CheckCircle2 className="h-12 w-12 mx-auto mb-3" style={{ color: "var(--sj-primary)" }} />
                        <p className="text-base font-semibold" style={{ color: "var(--sj-text)" }}>
                          {activeTpl.message || "Thank you for connecting with us! Someone from our team will reach out soon."}
                        </p>
                        <div className="mt-2 flex flex-wrap justify-center gap-2">
                          {activeTpl.send_sms && <span className="text-[11px] font-semibold rounded-full px-2.5 py-1" style={{ background: "#0B5E4E1A", color: "var(--sj-primary)" }}>SMS sent</span>}
                          {activeTpl.send_email && <span className="text-[11px] font-semibold rounded-full px-2.5 py-1" style={{ background: "#0B5E4E1A", color: "var(--sj-primary)" }}>Email sent</span>}
                        </div>
                        {activeTpl.info_qr_enabled && activeTpl.info_qr_link && (
                          <div className="mt-5 flex flex-col items-center">
                            <div className="p-3 rounded-2xl border-2 bg-white" style={{ borderColor: "var(--sj-primary)" }}>
                              <QRCodeCanvas value={activeTpl.info_qr_link} size={140} level="M" fgColor="#0B5E4E" />
                            </div>
                            <p className="mt-2 text-sm font-medium" style={{ color: "var(--sj-primary)" }}>{activeTpl.info_qr_label || "More about our church"}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>Enable the response to preview what visitors will see after submitting the Connect form.</p>
                    )}
                  </div>

                  <div className={`${CARD} p-6 space-y-4`} data-testid="resp-followup">
                    <div className="flex items-center gap-2.5">
                      <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#0B5E4E1A" }}>
                        <Bell className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
                      </span>
                      <div>
                        <h3 className="font-head text-base font-bold" style={{ color: "var(--sj-text)" }}>Follow-up SMS</h3>
                        <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>Automatically text new contacts later, inviting them to the next service.</p>
                      </div>
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="resp-followup-enabled">
                      <input type="checkbox" checked={!!resp.auto_followup.enabled} onChange={(e) => setAF({ enabled: e.target.checked })} className="h-4 w-4 accent-[#0B5E4E]" />
                      <span className="text-sm font-semibold" style={{ color: "var(--sj-text)" }}>Send a follow-up SMS to new contacts</span>
                    </label>
                    <div className="flex items-center gap-2 text-sm">
                      <span style={{ color: "var(--sj-text-2)" }}>Send</span>
                      <input type="number" min="1" data-testid="resp-followup-delay" value={resp.auto_followup.delay_hours} onChange={(e) => setAF({ delay_hours: Number(e.target.value) || 24 })}
                        className="w-20 bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 px-3 py-1.5 text-sm" />
                      <span style={{ color: "var(--sj-text-2)" }}>hours after they connect (24 = the next day)</span>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--sj-text-2)" }}>Follow-up message</label>
                      <textarea data-testid="resp-followup-message" rows={3} value={resp.auto_followup.message || ""} onChange={(e) => setAF({ message: e.target.value })}
                        placeholder="We loved having you connect with us! Do join us this Sunday at 10am & 12pm — we'd love to welcome you in person."
                        className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm px-4 py-2.5 text-sm" />
                    </div>

                    <div className="pt-3 border-t space-y-3" style={{ borderColor: "var(--sj-border)" }}>
                      <label className="flex items-center gap-3 cursor-pointer" data-testid="resp-second-enabled">
                        <input type="checkbox" checked={!!resp.auto_followup.second_enabled} onChange={(e) => setAF({ second_enabled: e.target.checked })} className="h-4 w-4 accent-[#0B5E4E]" />
                        <span className="text-sm font-semibold" style={{ color: "var(--sj-text)" }}>Send a gentle 2nd reminder later (only if they haven't visited yet)</span>
                      </label>
                      {resp.auto_followup.second_enabled && (
                        <>
                          <div className="flex items-center gap-2 text-sm">
                            <span style={{ color: "var(--sj-text-2)" }}>Send</span>
                            <input type="number" min="1" data-testid="resp-second-delay" value={resp.auto_followup.second_delay_hours} onChange={(e) => setAF({ second_delay_hours: Number(e.target.value) || 168 })}
                              className="w-24 bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 px-3 py-1.5 text-sm" />
                            <span style={{ color: "var(--sj-text-2)" }}>hours after connecting (168 = a week)</span>
                          </div>
                          <textarea data-testid="resp-second-message" rows={2} value={resp.auto_followup.second_message || ""} onChange={(e) => setAF({ second_message: e.target.value })}
                            placeholder="Just checking in — we'd still love to welcome you this Sunday. Hope to see you soon!"
                            className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm px-4 py-2.5 text-sm" />
                        </>
                      )}
                    </div>
                  </div>

                  <div className={`${CARD} p-6`} data-testid="resp-followup-log">
                    <h3 className="font-head text-base font-bold mb-3" style={{ color: "var(--sj-text)" }}>Follow-up SMS log</h3>
                    {followupLog.length === 0 ? (
                      <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>No follow-up messages have been sent yet. Once contacts reach the delay you set, they'll appear here.</p>
                    ) : (
                      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                        {followupLog.map((l, i) => (
                          <div key={i} data-testid={`followup-log-${i}`} className="flex items-center gap-3 rounded-xl px-3 py-2" style={{ background: "var(--sj-bg)" }}>
                            <Bell className="h-4 w-4 shrink-0" style={{ color: "var(--sj-primary)" }} />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold truncate" style={{ color: "var(--sj-text)" }}>{l.name || l.phone}</div>
                              <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{l.phone} · {fmtDate(l.at)}</div>
                            </div>
                            <span className="text-[11px] font-semibold rounded-full px-2.5 py-1 shrink-0" style={{ background: "#0B5E4E1A", color: "var(--sj-primary)" }}>{l.kind}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {shareQr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="qr-share-dialog" style={{ background: "rgba(0,0,0,0.4)" }} onClick={() => setShareQr(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "var(--sj-border)" }}>
              <div className="min-w-0">
                <h3 className="font-head text-lg font-bold truncate" style={{ color: "var(--sj-text)" }}>Share “{shareQr.name}”</h3>
                <p className="text-xs" style={{ color: "var(--sj-text-3)" }}>Choose who can see this QR code in their member portal.</p>
              </div>
              <button type="button" data-testid="qr-share-close" onClick={() => setShareQr(null)} className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-[var(--sj-bg)]" style={{ color: "var(--sj-text-3)" }}><X className="h-4 w-4" /></button>
            </div>

            <div className="px-6 py-4 space-y-4 overflow-y-auto">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border-2" data-testid="qr-share-everyone"
                style={{ borderColor: shareType === "everyone" ? "var(--sj-primary)" : "var(--sj-border)" }}>
                <input type="radio" name="share-type" checked={shareType === "everyone"} onChange={() => setShareType("everyone")} className="mt-0.5 h-4 w-4 accent-[#0B5E4E]" />
                <div>
                  <div className="text-sm font-semibold" style={{ color: "var(--sj-text)" }}>Whole church</div>
                  <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>Every member sees this on their Church QR codes page.</div>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border-2" data-testid="qr-share-custom"
                style={{ borderColor: shareType === "custom" ? "var(--sj-primary)" : "var(--sj-border)" }}>
                <input type="radio" name="share-type" checked={shareType === "custom"} onChange={() => setShareType("custom")} className="mt-0.5 h-4 w-4 accent-[#0B5E4E]" />
                <div>
                  <div className="text-sm font-semibold" style={{ color: "var(--sj-text)" }}>Specific teams &amp; members</div>
                  <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>Only the teams and people you pick will see it.</div>
                </div>
              </label>

              {shareType === "custom" && (
                <div className="space-y-4 pl-1">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--sj-text-3)" }}>Teams</p>
                    <div className="flex flex-wrap gap-2" data-testid="qr-share-teams">
                      {audiences.teams.length === 0 ? (
                        <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>No teams yet.</span>
                      ) : audiences.teams.map((t) => {
                        const on = shareTeams.includes(t.id);
                        return (
                          <button key={t.id} type="button" data-testid={`qr-share-team-${t.id}`} onClick={() => setShareTeams((a) => toggleIn(a, t.id))}
                            className="text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors"
                            style={on ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { color: "var(--sj-text-2)", borderColor: "var(--sj-border)" }}>
                            {on ? "✓ " : ""}{t.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--sj-text-3)" }}>Individual members</p>
                    {shareMembers.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2" data-testid="qr-share-selected-members">
                        {shareMembers.map((id) => {
                          const m = audiences.members.find((x) => x.id === id);
                          return (
                            <span key={id} className="inline-flex items-center gap-1 text-xs font-medium rounded-full pl-2.5 pr-1 py-1" style={{ background: "#0B5E4E1A", color: "var(--sj-primary)" }}>
                              {m?.name || "Member"}
                              <button type="button" onClick={() => setShareMembers((a) => a.filter((x) => x !== id))} className="h-4 w-4 inline-flex items-center justify-center rounded-full"><X className="h-3 w-3" /></button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <input data-testid="qr-share-member-search" value={memberQuery} onChange={(e) => setMemberQuery(e.target.value)}
                      placeholder="Search members by name…" className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 px-3 py-2 text-sm" />
                    {memberQuery.trim() && (
                      <div className="mt-2 max-h-44 overflow-y-auto rounded-xl border" style={{ borderColor: "var(--sj-border)" }}>
                        {audiences.members.filter((m) => m.name.toLowerCase().includes(memberQuery.trim().toLowerCase())).slice(0, 40).map((m) => {
                          const on = shareMembers.includes(m.id);
                          return (
                            <button key={m.id} type="button" data-testid={`qr-share-member-${m.id}`} onClick={() => setShareMembers((a) => toggleIn(a, m.id))}
                              className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-[var(--sj-bg)]" style={{ color: "var(--sj-text)" }}>
                              <span className="h-4 w-4 inline-flex items-center justify-center rounded border" style={{ borderColor: on ? "var(--sj-primary)" : "var(--sj-border)", background: on ? "var(--sj-primary)" : "#fff", color: "#fff" }}>{on ? "✓" : ""}</span>
                              {m.name}
                            </button>
                          );
                        })}
                        {audiences.members.filter((m) => m.name.toLowerCase().includes(memberQuery.trim().toLowerCase())).length === 0 && (
                          <div className="px-3 py-2 text-xs" style={{ color: "var(--sj-text-3)" }}>No members match.</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-6 py-4 border-t" style={{ borderColor: "var(--sj-border)" }}>
              {shareQr.published ? (
                <button type="button" data-testid="qr-share-stop" onClick={() => saveShare(false)} disabled={sharingSaving}
                  className="text-sm font-semibold hover:underline" style={{ color: "#B91C1C" }}>Stop sharing</button>
              ) : <span />}
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setShareQr(null)} className="rounded-full px-4 py-2 text-sm font-semibold border hover:bg-[var(--sj-bg)]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
                <button type="button" data-testid="qr-share-save" onClick={() => saveShare(true)} disabled={sharingSaving}
                  className="rounded-full px-5 py-2 text-sm font-semibold text-white hover:brightness-105 disabled:opacity-50" style={{ background: "var(--sj-primary)" }}>
                  {sharingSaving ? "Saving…" : (shareQr.published ? "Update sharing" : "Share")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
